<template>
  <div class="p-time-spinner" :class="{ 'has-seconds': showSeconds, 'has-ampm': amPmMode }">
    <template v-if="!arrowControl">
      <p-scrollbar
        @mouseenter.native="emitSelectRange('hours')"
        @mousemove.native="adjustCurrentSpinner('hours')"
        class="p-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="p-time-spinner__list"
        noresize
        tag="ul"
        ref="hours">
        <li
          @click="handleClick('hours', { value: hour, disabled: disabled })"
          v-for="(disabled, hour) in hoursList"
          class="p-time-spinner__item"
          :key="hour"
          :class="{ 'active': amPmMode ? hour === displayHour : hour === hours, 'disabled': disabled }">{{ ('0' + hour).slice(-2) }}</li>
      </p-scrollbar>
      <p-scrollbar
        @mouseenter.native="emitSelectRange('minutes')"
        @mousemove.native="adjustCurrentSpinner('minutes')"
        class="p-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="p-time-spinner__list"
        noresize
        tag="ul"
        ref="minutes">
        <li
          @click="handleClick('minutes', { value: key, disabled: false })"
          v-for="(enabled, key) in minutesList"
          :key="key"
          class="p-time-spinner__item"
          :class="{ 'active': key === minutes, disabled: !enabled }">{{ ('0' + key).slice(-2) }}</li>
      </p-scrollbar>
      <p-scrollbar
        v-show="showSeconds"
        @mouseenter.native="emitSelectRange('seconds')"
        @mousemove.native="adjustCurrentSpinner('seconds')"
        class="p-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="p-time-spinner__list"
        noresize
        tag="ul"
        ref="seconds">
        <li
          @click="handleClick('seconds', { value: key, disabled: false })"
          v-for="(second, key) in 60"
          class="p-time-spinner__item"
          :class="{ 'active': key === seconds }"
          :key="key">{{ ('0' + key).slice(-2) }}</li>
      </p-scrollbar>
      <p-scrollbar
        v-show="amPmMode"
        @mouseenter.native="emitSelectRange('ampm')"
        class="p-time-spinner__wrapper"
        wrap-style="max-height: inherit;"
        view-class="p-time-spinner__list"
        noresize
        tag="ul"
        ref="ampm">
        <li
          @click="handleClick('ampm', { value: 0, disabled: false })"
          class="p-time-spinner__item"
          :class="{ 'active': amPmValue === 0 }"
          :key="0">{{ amPmMode === 'A' ? 'AM' : 'am' }}</li>
        <li
          @click="handleClick('ampm', { value: 1, disabled: false })"
          class="p-time-spinner__item"
          :class="{ 'active': amPmValue === 1 }"
          :key="1">{{ amPmMode === 'A' ? 'PM' : 'pm' }}</li>
      </p-scrollbar>
    </template>
    <template v-if="arrowControl">
      <div
        @mouseenter="emitSelectRange('hours')"
        class="p-time-spinner__wrapper is-arrow">
        <i v-repeat-click="decrease" class="p-time-spinner__arrow p-icon-arrow-up"></i>
        <i v-repeat-click="increase" class="p-time-spinner__arrow p-icon-arrow-down"></i>
        <ul class="p-time-spinner__list" ref="hours">
          <li
            class="p-time-spinner__item"
            :class="{ 'active': amPmMode ? hour === displayHour : hour === hours, 'disabled': !amPmMode && hoursList[hour] }"
            v-for="(hour, key) in arrowHourList"
            :key="key">{{ hour === undefined ? '' : ('0' + hour).slice(-2) }}</li>
        </ul>
      </div>
      <div
        @mouseenter="emitSelectRange('minutes')"
        class="p-time-spinner__wrapper is-arrow">
        <i v-repeat-click="decrease" class="p-time-spinner__arrow p-icon-arrow-up"></i>
        <i v-repeat-click="increase" class="p-time-spinner__arrow p-icon-arrow-down"></i>
        <ul class="p-time-spinner__list" ref="minutes">
          <li
            class="p-time-spinner__item"
            :class="{ 'active': minute === minutes }"
            v-for="(minute, key) in arrowMinuteList"
            :key="key">
            {{ minute === undefined ? '' : ('0' + minute).slice(-2) }}
          </li>
        </ul>
      </div>
      <div
        @mouseenter="emitSelectRange('seconds')"
        class="p-time-spinner__wrapper is-arrow"
        v-if="showSeconds">
        <i v-repeat-click="decrease" class="p-time-spinner__arrow p-icon-arrow-up"></i>
        <i v-repeat-click="increase" class="p-time-spinner__arrow p-icon-arrow-down"></i>
        <ul class="p-time-spinner__list" ref="seconds">
          <li
            v-for="(second, key) in arrowSecondList"
            class="p-time-spinner__item"
            :class="{ 'active': second === seconds }"
            :key="key">
            {{ second === undefined ? '' : ('0' + second).slice(-2) }}
          </li>
        </ul>
      </div>
      <div
        @mouseenter="emitSelectRange('ampm')"
        class="p-time-spinner__wrapper is-arrow"
        v-if="amPmMode">
        <i v-repeat-click="decrease" class="p-time-spinner__arrow p-icon-arrow-up"></i>
        <i v-repeat-click="increase" class="p-time-spinner__arrow p-icon-arrow-down"></i>
        <ul class="p-time-spinner__list" ref="ampm">
          <li
            class="p-time-spinner__item"
            :class="{ 'active': amPmValue === 0 }">
            {{ amPmMode === 'A' ? 'AM' : 'am' }}
          </li>
          <li
            class="p-time-spinner__item"
            :class="{ 'active': amPmValue === 1 }">
            {{ amPmMode === 'A' ? 'PM' : 'pm' }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script type="text/babel">
  import { getRangeHours, getRangeMinutes, modifyTime } from '@/utils/date-util.js';
  import PScrollbar from '@/package-admin/p-scrollbar';
  import RepeatClick from '@/directives/repeat-click.js';

  export default {
    components: { PScrollbar },

    directives: {
      repeatClick: RepeatClick
    },

    props: {
      date: {},
      defaultValue: {}, // reserved for future use
      showSeconds: {
        type: Boolean,
        default: true
      },
      arrowControl: Boolean,
      amPmMode: {
        type: String,
        default: '' // 'a': am/pm; 'A': AM/PM
      }
    },

    computed: {
      hours() {
        return this.date.getHours();
      },
      minutes() {
        return this.date.getMinutes();
      },
      seconds() {
        return this.date.getSeconds();
      },
      amPmValue() {
        return this.hours < 12 ? 0 : 1;
      },
      displayHour() {
        // 用于显示的12小时制小时
        if (this.amPmMode) {
          const h = this.hours % 12;
          return h === 0 ? 12 : h;
        }
        return this.hours;
      },
      hoursList() {
        if (this.amPmMode) {
          // 12小时制：返回1-12
          const hours = [];
          for (let i = 1; i <= 12; i++) {
            hours[i] = false; // false表示不禁用
          }
          return hours;
        }
        return getRangeHours(this.selectableRange);
      },
      minutesList() {
        return getRangeMinutes(this.selectableRange, this.hours);
      },
      arrowHourList() {
        if (this.amPmMode) {
          const displayHour = this.displayHour;
          return [
            displayHour > 1 ? displayHour - 1 : 12,
            displayHour,
            displayHour < 12 ? displayHour + 1 : 1
          ];
        }
        const hours = this.hours;
        return [
          hours > 0 ? hours - 1 : undefined,
          hours,
          hours < 23 ? hours + 1 : undefined
        ];
      },
      arrowMinuteList() {
        const minutes = this.minutes;
        return [
          minutes > 0 ? minutes - 1 : undefined,
          minutes,
          minutes < 59 ? minutes + 1 : undefined
        ];
      },
      arrowSecondList() {
        const seconds = this.seconds;
        return [
          seconds > 0 ? seconds - 1 : undefined,
          seconds,
          seconds < 59 ? seconds + 1 : undefined
        ];
      }
    },

    data() {
      return {
        selectableRange: [],
        currentScrollbar: null
      };
    },

    mounted() {
      this.$nextTick(() => {
        !this.arrowControl && this.bindScrollEvent();
      });
    },

    methods: {
      increase() {
        this.scrollDown(1);
      },

      decrease() {
        this.scrollDown(-1);
      },

      modifyDateField(type, value) {
        switch (type) {
          case 'hours': {
            let actualHour = value;
            if (this.amPmMode) {
              // 将12小时制（1-12）转换为24小时制（0-23）
              if (value === 12) {
                actualHour = this.amPmValue === 0 ? 0 : 12;
              } else {
                actualHour = this.amPmValue === 0 ? value : value + 12;
              }
            }
            this.$emit('change', modifyTime(this.date, actualHour, this.minutes, this.seconds));
            break;
          }
          case 'minutes': this.$emit('change', modifyTime(this.date, this.hours, value, this.seconds)); break;
          case 'seconds': this.$emit('change', modifyTime(this.date, this.hours, this.minutes, value)); break;
          case 'ampm': {
            // value: 0 = AM, 1 = PM
            const currentHour = this.hours;
            let newHour;
            if (value === 0) {
              // Switch to AM
              newHour = currentHour >= 12 ? currentHour - 12 : currentHour;
            } else {
              // Switch to PM
              newHour = currentHour < 12 ? currentHour + 12 : currentHour;
            }
            this.$emit('change', modifyTime(this.date, newHour, this.minutes, this.seconds));
            break;
          }
        }
      },

      handleClick(type, {value, disabled}) {
        if (!disabled) {
          this.modifyDateField(type, value);
          this.emitSelectRange(type);
          this.adjustSpinner(type, value);
        }
      },

      emitSelectRange(type) {
        if (type === 'hours') {
          this.$emit('select-range', 0, 2);
        } else if (type === 'minutes') {
          this.$emit('select-range', 3, 5);
        } else if (type === 'seconds') {
          this.$emit('select-range', 6, 8);
        } else if (type === 'ampm') {
          const start = this.showSeconds ? 9 : 6;
          this.$emit('select-range', start, start + 2);
        }
        this.currentScrollbar = type;
      },

      bindScrollEvent() {
        const bindFunction = (type) => {
          if (this.$refs[type] && this.$refs[type].wrap) {
            this.$refs[type].wrap.onscroll = (e) => {
              // TODO: scroll is emitted when set scrollTop programatically
              // should find better solutions in the future!
              this.handleScroll(type, e);
            };
          }
        };
        bindFunction('hours');
        bindFunction('minutes');
        bindFunction('seconds');
        if (this.amPmMode) {
          bindFunction('ampm');
        }
      },

      handleScroll(type) {
        if (type === 'ampm') {
          // AM/PM 滚动处理：根据滚动位置决定是 0(AM) 还是 1(PM)
          const scrollTop = this.$refs[type].wrap.scrollTop;
          const itemHeight = this.typeItemHeight(type);
          // 如果滚动到下半部分，选择 PM，否则选择 AM
          const value = scrollTop > itemHeight / 2 ? 1 : 0;
          this.modifyDateField(type, value);
          return;
        }
        
        let maxValue = 59;
        if (type === 'hours') {
          maxValue = this.amPmMode ? 12 : 23;
        }
        let value = Math.min(Math.round((this.$refs[type].wrap.scrollTop - (this.scrollBarHeight(type) * 0.5 - 10) / this.typeItemHeight(type) + 3) / this.typeItemHeight(type)), maxValue);
        if (type === 'hours' && this.amPmMode) {
          // 在12小时制模式下，确保值在1-12范围内
          value = Math.max(1, Math.min(12, value));
        }
        this.modifyDateField(type, value);
      },

      // NOTE: used by datetime / date-range panel
      //       renamed from adjustScrollTop
      //       should try to refactory it
      adjustSpinners() {
        this.adjustSpinner('hours', this.hours);
        this.adjustSpinner('minutes', this.minutes);
        this.adjustSpinner('seconds', this.seconds);
        if (this.amPmMode) {
          this.adjustSpinner('ampm', this.amPmValue);
        }
      },

      adjustCurrentSpinner(type) {
        this.adjustSpinner(type, this[type]);
      },

      adjustSpinner(type, value) {
        if (this.arrowControl) return;
        if (!this.$refs[type] || !this.$refs[type].wrap) return;
        
        const el = this.$refs[type].wrap;
        
        if (type === 'ampm') {
          // AM/PM 滚动调整：0(AM) 或 1(PM)
          const itemHeight = this.typeItemHeight(type);
          el.scrollTop = this.amPmValue * itemHeight;
          return;
        }
        
        let scrollValue = value;
        if (type === 'hours' && this.amPmMode) {
          // 在12小时制模式下，使用 displayHour（1-12）
          scrollValue = this.displayHour;
        }
        el.scrollTop = Math.max(0, scrollValue * this.typeItemHeight(type));
      },

      scrollDown(step) {
        if (!this.currentScrollbar) {
          this.emitSelectRange('hours');
        }

        const label = this.currentScrollbar;
        let now = this[label];

        if (this.currentScrollbar === 'hours') {
          if (this.amPmMode) {
            // 12小时制模式：在1-12之间循环
            let displayHour = this.displayHour;
            displayHour = displayHour + step;
            if (displayHour > 12) displayHour = 1;
            if (displayHour < 1) displayHour = 12;
            this.modifyDateField('hours', displayHour);
            this.adjustSpinner('hours', displayHour);
          } else {
            // 24小时制模式：使用原有逻辑
            const hoursList = this.hoursList;
            let total = Math.abs(step);
            step = step > 0 ? 1 : -1;
            let length = hoursList.length;
            while (length-- && total) {
              now = (now + step + hoursList.length) % hoursList.length;
              if (hoursList[now]) {
                continue;
              }
              total--;
            }
            if (hoursList[now]) return;
            this.modifyDateField('hours', now);
            this.adjustSpinner('hours', now);
          }
        } else if (this.currentScrollbar === 'ampm') {
          // Toggle between AM (0) and PM (1)
          now = this.amPmValue === 0 ? 1 : 0;
          this.modifyDateField('ampm', now);
          return;
        } else {
          now = (now + step + 60) % 60;
          this.modifyDateField(label, now);
          this.adjustSpinner(label, now);
        }

        this.$nextTick(() => this.emitSelectRange(this.currentScrollbar));
      },
      typeItemHeight(type) {
        return this.$refs[type].$el.querySelector('li').offsetHeight;
      },
      scrollBarHeight(type) {
        return this.$refs[type].$el.offsetHeight;
      }
    }
  };
</script>
